import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Eye, Menu, X, LogOut, Shuffle, ChevronDown, CalendarDays, FlaskConical } from "lucide-react";

const PUBLIC_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/shop", label: "Shop" },
  { to: "/contact", label: "Contact" },
];

const MORE_LINKS = [
  { to: "/calendar", label: "Calendar" },
  { to: "/classes", label: "Classes & Sessions" },
  { to: "/certified-directors", label: "Certified Directors" },
  { to: "/team", label: "Our Team" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { sensoryFriendly, plainLanguage, toggleSensory, togglePlain } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    if (!agendaOpen && !moreOpen) return undefined;
    const close = () => { setAgendaOpen(false); setMoreOpen(false); };
    const onDown = (e) => { if (!e.target.closest("[data-nav-menu]")) close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [agendaOpen, moreOpen]);
  const moreLinks = user?.role === "admin" || user?.role === "director"
    ? [...MORE_LINKS, { to: "/director/toolbox", label: "Director Tool Box" }] : MORE_LINKS;

  const dashPath =
    user?.role === "admin" ? "/admin" : user?.role === "director" ? "/director" : "/participant";

  const promptPath =
    user?.role === "admin" || user?.role === "director"
      ? "/director/generator"
      : "/participant?tab=promptaneer";

  const frankPath =
    user?.role === "admin" || user?.role === "director"
      ? "/director/frankenstein"
      : "/participant?tab=frankenstein";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#f9f6f6]/92 border-b border-[#dfd8da]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-display text-lg sm:text-xl text-[#550001] tracking-tight leading-none">
              Improvaneer
            </span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#920e12] mt-1">
              Method
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {PUBLIC_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors duration-150 hover:bg-[#920e12] hover:text-white ${
                  location.pathname === l.to ? "text-[#920e12]" : "text-[#1a1617]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            {user && (
              <Link
                to={promptPath}
                data-testid="nav-link-promptaneer"
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors duration-150 text-[#920e12] hover:bg-[#920e12] hover:text-white"
              >
                <Shuffle size={15} aria-hidden="true" /> Spin For A Prompt
              </Link>
            )}
            {user && (
              <Link
                to={frankPath}
                data-testid="nav-link-frankenstein"
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors duration-150 text-[#920e12] hover:bg-[#920e12] hover:text-white"
              >
                <FlaskConical size={15} aria-hidden="true" /> Dr. Frankenstein
              </Link>
            )}
            {user && (
              <Link
                to="/games"
                data-testid="nav-link-games"
                className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors duration-150 hover:bg-[#920e12] hover:text-white ${location.pathname === "/games" ? "text-[#920e12]" : "text-[#1a1617]"}`}
              >
                Games
              </Link>
            )}
            {user && (
              <div className="relative" data-nav-menu>
                <button
                  onClick={() => setAgendaOpen((v) => !v)}
                  data-testid="nav-agendas"
                  aria-expanded={agendaOpen}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors duration-150 text-[#1a1617] hover:bg-[#920e12] hover:text-white"
                >
                  <CalendarDays size={15} aria-hidden="true" /> Agendas <ChevronDown size={13} />
                </button>
                {agendaOpen && (
                  <>
                    <div className="absolute left-0 top-full pt-1 w-52 z-50">
                      <div className="bg-white border border-[#dfd8da] rounded-lg shadow-lg py-1">
                        <Link to="/agendas/library" onClick={() => setAgendaOpen(false)} data-testid="nav-agendas-library" className="block px-4 py-2 text-sm text-[#1a1617] hover:bg-[#f9f6f6] hover:text-[#920e12]">Agenda Library</Link>
                        <Link to="/agendas/build" onClick={() => setAgendaOpen(false)} data-testid="nav-agendas-build" className="block px-4 py-2 text-sm text-[#1a1617] hover:bg-[#f9f6f6] hover:text-[#920e12]">Build Your Own</Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </nav>

          <div className="flex items-center gap-1.5">
            {/* Hamburger "More" menu (desktop) */}
            <div className="relative hidden lg:block" data-nav-menu>
              <button
                onClick={() => setMoreOpen((v) => !v)}
                data-testid="nav-more"
                aria-expanded={moreOpen}
                aria-label="More pages"
                className="flex items-center gap-1 px-2.5 py-2 text-sm font-semibold rounded-md transition-colors duration-150 text-[#1a1617] hover:bg-[#920e12] hover:text-white"
              >
                <Menu size={17} aria-hidden="true" /> More
              </button>
              {moreOpen && (
                <>
                  <div className="absolute right-0 top-full pt-1 w-56 z-50">
                    <div className="bg-white border border-[#dfd8da] rounded-lg shadow-lg py-1">
                      {moreLinks.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setMoreOpen(false)}
                          data-testid={`nav-more-${l.to.replace(/^\//, "").replace(/\//g, "-")}`}
                          className="block px-4 py-2 text-sm text-[#1a1617] hover:bg-[#f9f6f6] hover:text-[#920e12]">
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={toggleSensory}
              data-testid="toggle-sensory"
              aria-pressed={sensoryFriendly}
              title="Sensory-friendly mode (reduced motion)"
              className={`p-2 rounded-md border transition-colors duration-150 ${
                sensoryFriendly ? "bg-[#920e12] text-white border-[#920e12]" : "border-[#d6cecf] text-[#545454]"
              }`}
            >
              <Sparkles size={17} aria-hidden="true" />
              <span className="sr-only">Toggle sensory-friendly mode</span>
            </button>
            <button
              onClick={togglePlain}
              data-testid="toggle-plain-language"
              aria-pressed={plainLanguage}
              title="Plain language / easy read mode"
              className={`p-2 rounded-md border transition-colors duration-150 ${
                plainLanguage ? "bg-[#920e12] text-white border-[#920e12]" : "border-[#d6cecf] text-[#545454]"
              }`}
            >
              <Eye size={17} aria-hidden="true" />
              <span className="sr-only">Toggle plain language mode</span>
            </button>

            {user ? (
              <div className="hidden sm:flex items-center gap-1.5">
                <Button asChild size="sm" className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="nav-dashboard">
                  <Link to={dashPath}>Console</Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { logout(); navigate("/"); }}
                  data-testid="nav-logout"
                  className="border-[#d6cecf]"
                >
                  <LogOut size={15} aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <Button asChild size="sm" className="hidden sm:flex bg-[#920e12] hover:bg-[#550001] text-white" data-testid="nav-login">
                <Link to="/login">Log in</Link>
              </Button>
            )}

            <button
              className="lg:hidden p-2 text-[#1a1617]"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#dfd8da] bg-[#f9f6f6] px-4 py-3" data-testid="nav-mobile-menu">
          {PUBLIC_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm font-semibold rounded-md hover:bg-[#920e12] hover:text-white"
            >
              {l.label}
            </Link>
          ))}
          {user && (
            <Link
              to={promptPath}
              onClick={() => setOpen(false)}
              data-testid="nav-link-promptaneer-mobile"
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md text-[#920e12] hover:bg-[#920e12] hover:text-white"
            >
              <Shuffle size={15} aria-hidden="true" /> Spin For A Prompt
            </Link>
          )}
          {user && (
            <Link
              to={frankPath}
              onClick={() => setOpen(false)}
              data-testid="nav-link-frankenstein-mobile"
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md text-[#920e12] hover:bg-[#920e12] hover:text-white"
            >
              <FlaskConical size={15} aria-hidden="true" /> Dr. Frankenstein
            </Link>
          )}
          {user && (
            <Link to="/games" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm font-semibold rounded-md hover:bg-[#920e12] hover:text-white">Games</Link>
          )}
          <div className="px-3 pt-3 text-[10px] uppercase tracking-widest font-extrabold text-[#bf6163]">More</div>
          {moreLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              data-testid={`nav-more-mobile-${l.to.replace(/^\//, "").replace(/\//g, "-")}`}
              className="block px-5 py-2 text-sm font-semibold rounded-md hover:bg-[#920e12] hover:text-white">
              {l.label}
            </Link>
          ))}
          {user && (
            <>
              <div className="px-3 pt-3 text-[10px] uppercase tracking-widest font-extrabold text-[#bf6163]">Agendas</div>
              <Link to="/agendas/library" onClick={() => setOpen(false)} className="block px-5 py-2 text-sm font-semibold rounded-md hover:bg-[#920e12] hover:text-white">Agenda Library</Link>
              <Link to="/agendas/build" onClick={() => setOpen(false)} className="block px-5 py-2 text-sm font-semibold rounded-md hover:bg-[#920e12] hover:text-white">Build Your Own</Link>
            </>
          )}
          <div className="mt-2 flex gap-2">
            {user ? (
              <>
                <Button asChild size="sm" className="flex-1 bg-[#920e12] text-white"><Link to={dashPath} onClick={() => setOpen(false)}>Console</Link></Button>
                <Button size="sm" variant="outline" onClick={() => { logout(); navigate("/"); setOpen(false); }}>Log out</Button>
              </>
            ) : (
              <Button asChild size="sm" className="flex-1 bg-[#920e12] text-white"><Link to="/login" onClick={() => setOpen(false)}>Log in</Link></Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
